import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CanvasCardFrame, {
	CanvasCardDragSharedProps,
	getCanvasCardAccentBackground,
	getCanvasCardAccentForeground,
	getCanvasCardSurfaceBackground,
	useCanvasCardHighlighted,
} from './CanvasCardFrame';
import { useAppSelector } from '../hooks';
import { useRepositoryEntryCountQuery } from '../services/queries';
import { selectIsAuthenticated } from '../containers/authSlice';
import { resolveChartWhereForApi } from '../chart/resolveChartWhereForApi';
import { selectAllEntryProperties, selectAllRepositoryEntries, selectAllRepositories, selectAllPropertyDefinitions } from '../containers/contentSlice';
import { populateRepositoryEntriesForTable } from '../common/util';
import { filterOfflineRowsByArchivedAndDataFilter } from '../common/offlineDataFilter';

export interface CanvasQueryCardProps extends CanvasCardDragSharedProps {
	label: string;
	onMenuClick: (e: React.MouseEvent) => void;
	setMenuAnchorEl: (el: HTMLElement | null) => void;
	repositoryId: string;
	where?: string | null;
	/** When false, count excludes archived repository entries. */
	includeArchived?: boolean;
}

type CanvasQueryCardBodyProps = {
	label: string;
	collectionColor?: string;
	repositoryId: string;
	where?: string | null;
	includeArchived: boolean;
};

/** Must render inside CanvasCardFrame so useCanvasCardHighlighted() sees the provider. */
function CanvasQueryCardBody({
	label,
	collectionColor,
	repositoryId,
	where,
	includeArchived,
}: CanvasQueryCardBodyProps) {
	const theme = useTheme();
	const isHighlighted = useCanvasCardHighlighted();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allRepositories = useAppSelector(selectAllRepositories);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);
	const resolvedFilter = useMemo(() => resolveChartWhereForApi(where ?? undefined), [where]);
	const { data, isFetching } = useRepositoryEntryCountQuery(
		{ repositoryId, dataFilter: resolvedFilter, includeArchived },
		{
			skip: !isAuthenticated || !repositoryId,
		},
	);
	const localRepositoryEntryCount = useMemo(() => {
		if (isAuthenticated || !repositoryId) {
			return undefined;
		}
		const repository = allRepositories.find((r) => r.id === repositoryId);
		if (!repository) {
			return 0;
		}
		const rows = populateRepositoryEntriesForTable(repository, allRepositoryEntries, allPropertyDefinitions, allEntryProperties);
		return filterOfflineRowsByArchivedAndDataFilter(rows, includeArchived, resolvedFilter).length;
	}, [allRepositoryEntries, allPropertyDefinitions, allEntryProperties, allRepositories, includeArchived, isAuthenticated, repositoryId, resolvedFilter]);
	const repositoryEntryCount = isAuthenticated ? data : localRepositoryEntryCount;
	const isRepositoryEntryCountLoading = Boolean(isAuthenticated && isFetching);
	const showCountBadge = true;
	const countLabel = isRepositoryEntryCountLoading
		? '…'
		: repositoryEntryCount !== undefined
			? String(repositoryEntryCount)
			: '?';

	const circleBg = getCanvasCardAccentBackground(theme, { isHighlighted, collectionColor });
	const circleTextColor = getCanvasCardAccentForeground(theme, { isHighlighted, collectionColor });
	const labelBg = getCanvasCardSurfaceBackground(theme, { isHighlighted, collectionColor });

	return (
		<Box
			display="flex"
			alignItems="center"
			sx={{ minWidth: 0, maxWidth: 220 }}
		>
			{showCountBadge && (
				<Box
					aria-hidden
					sx={{
						flexShrink: 0,
						width: 42,
						height: 42,
						borderRadius: 2,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: circleBg,
						zIndex: 2,
						boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)',
					}}
					component={Paper}
				>
					<Typography
						component="span"
						variant="caption"
						sx={{
							fontWeight: 600,
							lineHeight: 1,
							color: circleTextColor,
						}}
					>
						{countLabel}
					</Typography>
				</Box>
			)}
			<Box
				component={Paper}
				sx={{
					flex: 1,
					minWidth: 0,
					px: 1.5,
					py: 1,
					pl: 3,
					borderRadius: 2,
					ml: showCountBadge ? -2 : 0,
					backgroundColor: labelBg,
					zIndex: 1,
					position: 'relative',
					boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)',
				}}
			>
				<Typography
					variant="body2"
					sx={{
						minWidth: 0,
						wordBreak: 'break-word',
						lineHeight: 1.35,
					}}
					title={label}
				>
					{label || 'Untitled'}
				</Typography>
			</Box>
		</Box>
	);
}

function CanvasQueryCardBase({
	label,
	position,
	isSelected,
	scale,
	collectionColor,
	isPositionLocked,
	onPositionChange,
	constrainPosition,
	onDragStart,
	onDragActivate,
	onDragMove,
	onDragEnd,
	cardNodeRef,
	onFocus,
	onBlur,
	onSelect,
	repositoryId,
	where,
	includeArchived = true,
}: CanvasQueryCardProps) {
	return (
		<CanvasCardFrame
			position={position}
			scale={scale}
			isSelected={isSelected}
			contentShell="none"
			isPositionLocked={isPositionLocked}
			onPositionChange={onPositionChange}
			constrainPosition={constrainPosition}
			onDragStart={onDragStart}
			onDragActivate={onDragActivate}
			onDragMove={onDragMove}
			onDragEnd={onDragEnd}
			cardNodeRef={cardNodeRef}
			onFocus={onFocus}
			onBlur={onBlur}
			onSelect={onSelect}
			ariaLabel={label || 'Untitled'}
		>
			<CanvasQueryCardBody
				label={label}
				collectionColor={collectionColor}
				repositoryId={repositoryId}
				where={where}
				includeArchived={includeArchived}
			/>
		</CanvasCardFrame>
	);
}

/** Memoized so panning/zooming (which re-renders CanvasView every frame) doesn't also re-render every query card — CanvasView keeps this card's props referentially stable across those frames specifically so this bail-out holds. */
export const CanvasQueryCard = React.memo(CanvasQueryCardBase);
export default CanvasQueryCard;
